import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import { MapPin, Eye, Edit, Maximize2 } from 'lucide-react';
import type { Farm } from '../../types/orchard';

interface FarmCardProps {
  farm: Farm;
}

function FarmCard({ farm }: FarmCardProps) {
  const navigate = useNavigate();

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-neutral-900 truncate">{farm.name}</h3>
            {farm.location && (
              <p className="text-sm text-neutral-500 truncate flex items-center gap-1 mt-0.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                {farm.location}
              </p>
            )}
          </div>
          <div className="flex-shrink-0 ml-4">
            <div className="h-10 w-10 rounded-lg bg-success-100 flex items-center justify-center">
              <Maximize2 className="h-5 w-5 text-success-600" />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-neutral-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-neutral-900">
              {farm.total_area_hectares != null ? Number(farm.total_area_hectares).toFixed(1) : '—'}
            </p>
            <p className="text-xs text-neutral-500">Hectares</p>
          </div>
          <div className="bg-primary-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-primary-700">{farm.block_count ?? 0}</p>
            <p className="text-xs text-primary-600">Blocks</p>
          </div>
          <div className="bg-success-50 rounded-lg p-3 text-center">
            <p className="text-lg font-bold text-success-700">{farm.cultivar_area_count ?? 0}</p>
            <p className="text-xs text-success-600">Cultivars</p>
          </div>
        </div>

        {farm.description && (
          <p className="text-sm text-neutral-600 line-clamp-2 mb-4">{farm.description}</p>
        )}

        {/* Status */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
            farm.active
              ? 'bg-success-100 text-success-700'
              : 'bg-neutral-100 text-neutral-600'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${farm.active ? 'bg-success-500' : 'bg-neutral-400'}`} />
            {farm.active ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/orchard/farms/${farm.id}`)}
            className="flex-1"
          >
            <Eye className="h-4 w-4" />
            View Farm
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/orchard/farms/${farm.id}/edit`)}
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default FarmCard;
